import React, {useState, useEffect} from 'react'
import {DataLink} from '@dv5/domain'
import {dataLinksApi} from '@dv5/api'
import {useAppDispatch} from '@dv5/domain'
import {ErrorDisplayProcessor} from "../ErrorDisplayProcessor"
import {Input} from "../inputs/Input"
import {Button} from "../inputs/Button"
import {DataLinkItem} from './DataLinkItem'
import './DataLinkDialog.less'

interface DataLinkDialogProps {
    sourceItemId: string
    dataLinkCount: number
    onNavigate: (dataLink: DataLink) => void
    onDelete: (dataLink: DataLink, sourceItemId: string) => void
    quickCreation?: boolean
}

export const DataLinkDialog: React.FC<DataLinkDialogProps> = ({
                                                                  sourceItemId,
                                                                  dataLinkCount,
                                                                  onNavigate,
                                                                  onDelete,
                                                                  quickCreation = false,
                                                              }) => {
    const dispatch = useAppDispatch()
    const [dataLinks, setDataLinks] = useState<DataLink[]>([])
    const [isLoading, setIsLoading] = useState(true)
    const [editingId, setEditingId] = useState<string | null>(null)
    const [showQuickCreation, setShowQuickCreation] = useState(false)
    const [quickTitle, setQuickTitle] = useState('')
    const [quickUrl, setQuickUrl] = useState('')
    const [isQuickCreating, setIsQuickCreating] = useState(false)
    const [quickCreationError, setQuickCreationError] = useState('')

    useEffect(() => {
        const errorProcessor = new ErrorDisplayProcessor(dispatch)

        setIsLoading(true)
        dataLinksApi.getForSourceItem(
            sourceItemId,
            errorProcessor,
            (loaded) => {
                setDataLinks(loaded)
                setIsLoading(false)
            }
        ).catch((error) => {
            console.error('Failed to load data links:', error)
            setIsLoading(false)
        })
    }, [sourceItemId, dispatch])

    const deleteDataLink = (dataLink: DataLink) => {
        onDelete(dataLink, sourceItemId)
        setDataLinks(dataLinks.filter(dl => dl.id !== dataLink.id))
    }

    const renameDataLink = (dataLink: DataLink, newTitle: string) => {
        const errorProcessor = new ErrorDisplayProcessor(dispatch)
        dataLinksApi.renameDataLink(
            dataLink.id,
            newTitle,
            errorProcessor,
            (updatedDataLink) => {
                setDataLinks(dataLinks.map(dl => dl.id === updatedDataLink.id ? updatedDataLink : dl))
            }
        ).catch(console.error)
    }

    const handleQuickCreate = () => {
        const trimmedTitle = quickTitle.trim()
        const trimmedUrl = quickUrl.trim()
        if (!trimmedTitle) {
            setQuickCreationError('Title is required')
            return
        }
        if (!trimmedUrl) {
            setQuickCreationError('URL is required')
            return
        }
        setIsQuickCreating(true)
        const errorProcessor = new ErrorDisplayProcessor(dispatch)
        dataLinksApi.createQuickDataLink(
            sourceItemId,
            trimmedTitle,
            trimmedUrl,
            errorProcessor,
            (newDataLink) => {
                setDataLinks([newDataLink, ...dataLinks])
                setQuickTitle('')
                setQuickUrl('')
                setQuickCreationError('')
                setIsQuickCreating(false)
            }
        ).catch((error) => {
            console.error('Failed to create data link:', error)
            setQuickCreationError('Failed to create data link')
            setIsQuickCreating(false)
        })
    }

    const handleQuickCancel = () => {
        setQuickTitle('')
        setQuickUrl('')
        setQuickCreationError('')
    }
    const toggleCreate = () => {
        setShowQuickCreation(!showQuickCreation)
    }

    return (
        <div className="data-link-dialog-content">
            <h3>Data Links</h3>

            {quickCreation && (
                <div className="quick-creation-form">
                    <div className="quick-creation-toggle">
                        <Button
                            title={showQuickCreation ? 'Collapse' : 'Create New Link'}
                            onClick={toggleCreate}
                            className="expand-button"
                        />
                    </div>
                    {showQuickCreation && (
                        <>
                            <h4>Create New Link</h4>
                            <div className="quick-creation-inputs">
                                <Input
                                    id="quick-link-title"
                                    label="Title"
                                    value={quickTitle}
                                    placeholder="Enter link title"
                                    autoFocus={true}
                                    setValue={setQuickTitle}
                                    onConfirm={handleQuickCreate}
                                    onCancel={handleQuickCancel}
                                />
                                <Input
                                    id="quick-link-url"
                                    label="URL"
                                    value={quickUrl}
                                    placeholder="https://example.com"
                                    setValue={setQuickUrl}
                                    onConfirm={handleQuickCreate}
                                    onCancel={handleQuickCancel}
                                />
                            </div>
                            {quickCreationError && (
                                <div className="quick-creation-error">{quickCreationError}</div>
                            )}
                            <div className="quick-creation-buttons">
                                <Button title="Cancel" onClick={handleQuickCancel} className="cancel-dialog-button" />
                                <Button title="Create" onClick={handleQuickCreate} className="save-dialog-button" disabled={isQuickCreating} />
                            </div>
                        </>
                    )}
                </div>
            )}

            {isLoading ? (
                <p>Loading...</p>
            ) : dataLinks.length > 0 ? (
                <div className="data-links-list">
                    {dataLinks.map((link) => (
                        <DataLinkItem key={link.id} dataLink={link} onNavigate={onNavigate} onDelete={deleteDataLink} onEdit={renameDataLink}/>
                    ))}
                </div>
            ) : (
                <p>No data links found</p>
            )}
        </div>
    )
}
